import {
    buildOptionalParams,
    buildPath,
    buildTemplatePath,
    CrudMode,
    Dialog,
    handleClose,
    PageRoute,
    ScreenType,
    useParams,
    useUnsavedChanges,
    useValidationErrors,
} from "@compass/core";
import {
    ProviderPosition,
    ProviderPositionEditResource,
    ProviderPositionViewResource,
    Permission
} from "generated/openApi";
import { createContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { ProviderPositionPageHeader } from "./ProviderPositionPageHeader";
import { ProviderPositionPageBody } from "./ProviderPositionPageBody";

const routeName = "providerPosition";

class Params {
    id: number = 0;
    crudMode: CrudMode = "view";
    screenType: ScreenType = "standard";
}

class OptionalParams {
    closeOnSave?: boolean = false;
}

export const providerPositionPageRoute: PageRoute<Params, OptionalParams> = {
    handle: "Provider Position",
    templatePath: buildTemplatePath(routeName, Params),
    getPath: (params) => buildPath(routeName, params),
    getOptionalParams: (params) => buildOptionalParams(params),
};

export type ProviderPositionContextValue = {
    onCreate?: (id: number) => void;
};

export const ProviderPositionContext = createContext<ProviderPositionContextValue>({} as ProviderPositionContextValue);

export const ProviderPositionPage = () => {
    const ROOT_PERMISSION: Permission = Permission.MatchUtility;
    const navigate = useNavigate();
    const { params, optionalParams } = useParams(routeName, Params, OptionalParams);
    const [providerPosition, setProviderPosition] = useState<ProviderPosition>({} as ProviderPosition);
    const [initProviderPosition, setInitProviderPosition] = useState<ProviderPosition>(providerPosition);
    const [viewResource, setViewResource] = useState<ProviderPositionViewResource>({});
    const [editResource, setEditResource] = useState<ProviderPositionEditResource>({});
    const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [providerPosition] });
    const { validationErrors, setValidationErrors } = useValidationErrors();
    const [closeOnSave] = useState<boolean>(optionalParams.closeOnSave || false);

    useEffect(() => {
        fetch(params.id);

        ApiService.providers.getProviderPositionViewResource().then((response) => {
            setViewResource(response);
        });
    }, []);

    useEffect(() => {
        if (params.crudMode === "edit") {
            ApiService.providers.getProviderPositionEditResource().then((response) => {
                setEditResource(response);
            });
        }
    }, [params.crudMode]);

    const reroute = (id: number, crudMode: CrudMode) =>
        navigate(`../${providerPositionPageRoute.getPath({ id: id, crudMode: crudMode, screenType: params.screenType, })}`);

    const fetch = (id: number) => {
        if (id > 0) {
            ApiService.providers.getProviderPosition(id).then((response) => {
                setProviderPosition(response);
                setInitProviderPosition(response);
                resetUnsavedChanges([response]);
            });
        } else {
            setProviderPosition({} as ProviderPosition);
            setInitProviderPosition({} as ProviderPosition);
            resetUnsavedChanges([{} as ProviderPosition]);
        }
    };

    const pageHeader = (
        <ProviderPositionPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={providerPositionPageRoute.handle}
            crudMode={params.crudMode}
            reroute={reroute}
            fetch={fetch}
            providerPosition={providerPosition}
            setProviderPosition={setProviderPosition}
            initProviderPosition={initProviderPosition}
            setValidationErrors={setValidationErrors}
            unsavedChanges={unsavedChanges}
            resetUnsavedChanges={resetUnsavedChanges}
            closeOnSave={closeOnSave}
        />
    );

    const pageBody = (
        <ProviderPositionPageBody
            providerPosition={providerPosition}
            setProviderPosition={setProviderPosition}
            viewResource={viewResource}
            editResource={editResource}
            crudMode={params.crudMode}
            validationErrors={validationErrors}
        />
    );

    return (
        <Dialog
            isForm
            open={true}
            onClose={() => handleClose(navigate, unsavedChanges)}
            maxWidth="md"
            header={pageHeader}
            content={pageBody}
        />
    );
};